import { format, Interval, isBefore, isDate, isValid, parseISO, setHours, setMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

type FormattableDate = Date | string | number;

const DATE_TIME_FORMAT = 'dd.MM.yyyy HH:mm';
const DATE_FORMAT = 'dd.MM.yyyy';
const TIME_FORMAT = 'HH:mm';

export type DateInput = string | Date | undefined;

export const parseStringDate = (ISODateString: string): Date => {
    const date = parseISO(ISODateString);
    if (isValid(date)) {
        return date;
    } else {
        throw new Error(`Unexpected date format: ${ISODateString}`);
    }
};

export const parseOptionalStringDate = (ISODateString: string | undefined): Date | undefined =>
    ISODateString === undefined ? undefined : parseStringDate(ISODateString);

export const formatDateTimeInTimeZone = (dateTime: FormattableDate, timeZone: string): string =>
    formatInTimeZone(dateTime, timeZone, DATE_TIME_FORMAT);

export const formatDateInTimeZone = (dateTime: FormattableDate, timeZone: string): string =>
    formatInTimeZone(dateTime, timeZone, DATE_FORMAT);

export const formatTimeInTimeZone = (dateTime: FormattableDate, timeZone: string): string =>
    formatInTimeZone(dateTime, timeZone, TIME_FORMAT);

export const formatTimeZone = (date: FormattableDate, timeZone: string): string =>
    formatInTimeZone(date, timeZone, 'OOO');

export const isValidDate = (value: any): value is Date => isDate(value) && isValid(value);

export const areDatesEqual = (left: Date | string | undefined, right: Date | string | undefined) => {
    if (left instanceof Date && right instanceof Date) {
        return left.getTime() === right.getTime();
    }

    return left === right;
};

export const toTimestamp = (value: Date | number | string) => new Date(value).getTime();

export const getTimeAsString = (date: Date | undefined) => (date ? format(date, 'HH:mm') : '');

export const applyTimeToDate = (date: Date | undefined, timeValue: string) => {
    if (!date || !timeValue) {
        return date;
    }

    const [hours, minutes] = timeValue.split(':').map(Number);
    return setMinutes(setHours(date, hours), minutes);
};

export const areIntervalsEqual = (left: Interval | undefined, right: Interval | undefined) => {
    if (left === undefined || right === undefined) {
        return left === right;
    }

    return toTimestamp(left.start) === toTimestamp(right.start) && toTimestamp(left.end) === toTimestamp(right.end);
};

export const isDateStringEmpty = (date: DateInput): boolean => date === undefined || date.toString().length === 0;

export const dateMinusSeconds = (date: Date, seconds: number): Date => new Date(date.getTime() - seconds * 1000);

export const validateDateTimeFields = (startDate: DateInput, endDate: DateInput) => {
    const hasStartDateTimeError = startDate !== undefined && !isValidDate(startDate);
    const hasEndDateTimeError = endDate !== undefined && !isValidDate(endDate);

    const hasTimeRangeOrderError =
        !(hasEndDateTimeError || hasStartDateTimeError) &&
        endDate !== undefined &&
        startDate !== undefined &&
        isBefore(endDate, startDate);

    return {
        hasEndDateTimeError,
        hasStartDateTimeError,
        hasTimeRangeOrderError,
    };
};
